/// <reference types="@angular/localize" />

import {
  enableProdMode,
  isDevMode,
  provideZoneChangeDetection,
} from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withHashLocation } from '@angular/router';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';

import { AppComponent } from './app/app.component';
import { omsFeatureShellRoutes } from '@surface-elements/oms/feature-shell';
import {
  AppEffects,
  AuthInterceptor,
  HttpErrorAlertInterceptor,
  HttpErrorLoggingInterceptor,
  provideRootStores,
} from '@surface-elements/shared/data-access';
import { provideAlertStore } from '@surface-elements/shared/alert-system/data-access';
import { environment } from '@surface-elements/shared/environments';
import { ENVIRONMENT_TOKEN } from '@surface-elements/shared/util-logging';
import { ERROR_TRANSLATION_CONFIG } from '@surface-elements/shared/util-error';
import { OMS_ERROR_CONFIG } from './app/config/error-translations.config';
import { ORDER_ERROR_TRANSLATIONS } from '@surface-elements/orders/domain';
import { ACCOUNT_ERROR_TRANSLATIONS } from '@surface-elements/accounts/domain';
import { JOB_ERROR_TRANSLATIONS } from '@surface-elements/jobs/domain';
import { NgxMaskOptions, provideEnvironmentNgxMask } from 'ngx-mask';
import { NgbConfig } from '@ng-bootstrap/ng-bootstrap';

if (environment.production) {
  enableProdMode();
}

// ngx-mask global config, re-homed to the app root in SEOMS-412.
const maskConfig: NgxMaskOptions = {
  validation: false,
};

bootstrapApplication(AppComponent, {
  providers: [
    // Router + hash location (SEOMS-427: was RouterModule.forRoot in the shell shim).
    provideZoneChangeDetection(),
    provideRouter(omsFeatureShellRoutes, withHashLocation()),
    // Animations for the alert system (was BrowserAnimationsModule).
    provideAnimations(),
    // Disable ng-bootstrap's own collapse/accordion animation app-wide. It is
    // JS-driven and only clears the transient `.collapsing` state (overflow:hidden)
    // on `transitionend`; in headless CI there is no transitionend, so animated
    // order-form panels get stuck collapsed/clipped and their inputs never become
    // visible (SEOMS-458 e2e failures). Synchronous toggling removes that entire
    // failure class and matches the unit-test setup (NgbConfig.animation = false).
    {
      provide: NgbConfig,
      useFactory: () => {
        const config = new NgbConfig();
        config.animation = false;
        return config;
      },
    },
    // HTTP client + the 3 DI-based interceptors, order matters
    // (auth attaches JWT → error logging → error alert). withInterceptorsFromDi()
    // is required because all three are class-based HttpInterceptors.
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorLoggingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorAlertInterceptor,
      multi: true,
    },
    // Store / effects / devtools — fully standalone providers (SEOMS-437).
    // SEOMS-427 had to keep an interim StoreModule.forRoot here because the
    // feature slices still registered via StoreModule.forFeature (which requires
    // the NgModule StoreRootModule that provideStore never instantiates). Now
    // every slice is provideState/provideEffects (provideRootStores +
    // provideAlertStore), so the root is standalone provideStore.
    provideStore(
      {},
      {
        metaReducers: [],
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: true,
        },
      },
    ),
    provideEffects([AppEffects]),
    provideStoreDevtools({
      name: 'OMS DevTools',
      maxAge: 25,
      logOnly: !isDevMode(),
      connectInZone: true,
    }),
    provideRootStores(),
    provideAlertStore(),
    provideEnvironmentNgxMask(maskConfig),
    { provide: ENVIRONMENT_TOKEN, useValue: environment },
    // Error translations — order matters (lowest to highest priority).
    {
      provide: ERROR_TRANSLATION_CONFIG,
      useValue: {
        translations: ORDER_ERROR_TRANSLATIONS,
        appName: 'orders-domain',
      },
      multi: true,
    },
    {
      provide: ERROR_TRANSLATION_CONFIG,
      useValue: {
        translations: ACCOUNT_ERROR_TRANSLATIONS,
        appName: 'accounts-domain',
      },
      multi: true,
    },
    {
      provide: ERROR_TRANSLATION_CONFIG,
      useValue: {
        translations: JOB_ERROR_TRANSLATIONS,
        appName: 'jobs-domain',
      },
      multi: true,
    },
    {
      provide: ERROR_TRANSLATION_CONFIG,
      useValue: OMS_ERROR_CONFIG,
      multi: true,
    },
  ],
}).catch(err => console.error(err));
